import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";

import { Popup } from "react-map-gl/maplibre";
import { Record as RecordIcon } from "@phosphor-icons/react";
import { primitiveUtils } from "@local-logic/core/utils";

import type { School, SchoolAttributes } from "@local-logic/client";
import type { PopupProps } from "../types";
import * as styles from "../styles";
import "./popup.css";

export const MapGLPopup = ({
  marker,
  icon,
  onClose,
  onLinkClick,
}: PopupProps) => {
  const { t } = useTranslation(["common"]);

  const Icon = useMemo(
    () => (typeof icon !== "undefined" ? icon : RecordIcon),
    [icon]
  );

  if (!marker) {
    return null;
  }

  const formatAttribute = (attribute?: string | string[]) => {
    if (!attribute) return undefined;

    if (typeof attribute === "string") {
      return attribute;
    }

    const upperAtt = attribute.map((value) =>
      primitiveUtils.string.capitalize(value)
    );

    return upperAtt.join(", ");
  };

  const handleOnLinkClick = (url: string) => () => {
    if (typeof onLinkClick !== "undefined") {
      onLinkClick(url);
    }
  };

  return (
    <Popup
      anchor="left"
      latitude={marker.lat}
      longitude={marker.lng}
      closeButton={false}
      closeOnClick
      onClose={onClose}
      className="maplibre-popup"
    >
      <div className={styles.container}>
        <span className={styles.iconContainer}>
          <Icon className={styles.icon} />
        </span>
        {marker.url ? (
          <a
            className={styles.title}
            data-has-url={marker.url !== undefined}
            href={marker.url}
            onClick={handleOnLinkClick(marker.url)}
            target="_blank"
            rel="noreferrer"
          >
            {marker.name}
          </a>
        ) : (
          <h3 className={styles.title}>{marker.name}</h3>
        )}

        <p className={styles.description}>{marker.address}</p>

        {Object.hasOwn(marker, "school_attributes") && (
          <div>
            {Object.entries((marker as School).school_attributes || {}).map(
              ([key, schoolAttribute]) => (
                <p className={styles.schoolAttributeInfo} key={key}>
                  {`${t(`common:map.marker.schoolAttributes.${key as keyof SchoolAttributes}`)}: ${formatAttribute(schoolAttribute)}`}
                </p>
              )
            )}
          </div>
        )}

        {"school_board_attributes" in marker &&
          marker.school_board_attributes?.name && (
            <p>
              {`${t(`common:map.marker.district`)}: ${marker.school_board_attributes.name}`}
            </p>
          )}
      </div>
    </Popup>
  );
};
